import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import PageHeader from "../../../../components/common/PageHeader";
import PageLoader from "../../../../components/common/PageLoader";
import StatusChip from "../../../../components/common/StatusChip";
import ChipList from "../../../../components/common/ChipList";
import ChangePasswordDialog from "../ChangePasswordDialog";
import UpdateRoleDialog from "../UpdateRoleDialog";
import { USER_TYPES } from "../../constants";
import { systemUserService } from "../../services/systemUser.service";
import { tenantUserService } from "../../services/tenantUser.service";
import { roleService } from "../../services/role.service";
import { tenantRoleService } from "../../../role-management/services/tenantRole.service";
import { normalizeRoleList } from "../../../role-management/utils/normalizeRole";
import {
  formatEpochDate,
  normalizeUser,
} from "../../utils/normalizeUser";
import { maskEmail, maskMobile } from "../../../../helpers/mask.helper";
import { appActions } from "../../../../redux/actions";
import { getApiErrorMessage } from "../../../../helpers";
import useAuth from "../../../../hooks/useAuth";

const DetailItem = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, mt: 0.25, wordBreak: "break-word" }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

const UserDetailsPage = ({ userType = USER_TYPES.TENANT }) => {
  const isSystem = userType === USER_TYPES.SYSTEM;
  const { userId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { activeTenantId, tenantId: authTenantId } = useAuth();
  const tenantId = activeTenantId || authTenantId;
  const listPath = isSystem ? "/systemusers" : "/tenantusers";

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [roleLoading, setRoleLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const loadUser = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError("");
    try {
      const detail = isSystem
        ? await systemUserService.getSystemUserById(userId)
        : await tenantUserService.getTenantUserById(userId, tenantId);
      setUser(normalizeUser(detail || {}));
    } catch (err) {
      setUser(null);
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [isSystem, tenantId, userId]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const loadRoles = useCallback(async () => {
    setRolesLoading(true);
    try {
      if (isSystem) {
        setRoles(await roleService.getAllRoles());
      } else {
        const payload = await tenantRoleService.getAllTenantRoles(tenantId);
        setRoles(
          normalizeRoleList(payload)
            .map((role) => role.name)
            .filter(Boolean)
        );
      }
    } catch {
      setRoles([]);
    } finally {
      setRolesLoading(false);
    }
  }, [isSystem, tenantId]);

  const handleChangePassword = async (newPassword) => {
    if (!user?.userId) return;
    setPasswordLoading(true);
    try {
      if (isSystem) {
        await systemUserService.changeSystemUserPassword(user.userId, newPassword);
      } else {
        await tenantUserService.changeTenantUserPassword(
          user.userId,
          newPassword,
          tenantId
        );
      }
      dispatch(appActions.apiSuccessMessage("Password updated successfully."));
      setPasswordOpen(false);
    } catch (err) {
      throw new Error(getApiErrorMessage(err));
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleUpdateRole = async (nextRoles) => {
    if (!user?.userId) return;
    setRoleLoading(true);
    try {
      if (isSystem) {
        await systemUserService.updateSystemUserRole(user.userId, nextRoles);
      } else {
        await tenantUserService.updateTenantUserRole(
          user.userId,
          nextRoles,
          tenantId
        );
      }
      setUser((prev) => normalizeUser({ ...prev, roles: nextRoles }));
      dispatch(appActions.apiSuccessMessage("Roles updated successfully."));
      setRoleOpen(false);
    } catch (err) {
      throw new Error(getApiErrorMessage(err));
    } finally {
      setRoleLoading(false);
    }
  };

  const handleToggleMfa = async (mfaEnabled) => {
    if (!user?.userId || isSystem) return;
    setSaving(true);
    try {
      await tenantUserService.updateTenantUser(
        user.userId,
        { mfaEnabled },
        tenantId
      );
      setUser((prev) => normalizeUser({ ...prev, mfaEnabled }));
      dispatch(appActions.apiSuccessMessage("MFA updated successfully."));
    } catch (err) {
      dispatch(appActions.apiErrorMessage(getApiErrorMessage(err)));
    } finally {
      setSaving(false);
    }
  };

  const handleUnlock = async () => {
    if (!user?.userId || isSystem) return;
    setSaving(true);
    try {
      await tenantUserService.updateTenantUserStatus(
        user.userId,
        "unlock",
        tenantId
      );
      setUser((prev) => normalizeUser({ ...prev, locked: false }));
      dispatch(appActions.apiSuccessMessage("User unlocked successfully."));
    } catch (err) {
      dispatch(appActions.apiErrorMessage(getApiErrorMessage(err)));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!user?.userId) return;
    setSaving(true);
    try {
      if (isSystem) {
        await systemUserService.deleteSystemUser(user.userId);
      } else {
        await tenantUserService.deleteTenantUser(user.userId, tenantId);
      }
      dispatch(appActions.apiSuccessMessage("User deleted successfully."));
      setDeleteOpen(false);
      navigate(listPath);
    } catch (err) {
      dispatch(appActions.apiErrorMessage(getApiErrorMessage(err)));
    } finally {
      setSaving(false);
    }
  };

  const openRoleDialog = async () => {
    setRoleOpen(true);
    await loadRoles();
  };

  if (loading) {
    return <PageLoader text="Loading user..." />;
  }

  if (error && !user) {
    return (
      <Box>
        <Button startIcon={<ArrowBackOutlinedIcon />} onClick={() => navigate(listPath)}>
          Back to users
        </Button>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!user) return null;

  return (
    <Box sx={{ width: "100%" }}>
      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
        <Button startIcon={<ArrowBackOutlinedIcon />} onClick={() => navigate(listPath)}>
          Back to users
        </Button>
      </Stack>
      <PageHeader title="User Details" subtitle={user.username || user.userId} />

      <Paper
        elevation={0}
        sx={{ p: 3, border: "1px solid", borderColor: "divider", mb: 2 }}
      >
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Basic Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <DetailItem label="User ID" value={user.userId} />
              </Grid>
              <Grid item xs={12} md={6}>
                <DetailItem label="Username" value={user.username} />
              </Grid>
              <Grid item xs={12} md={6}>
                <DetailItem label="First Name" value={user.firstname} />
              </Grid>
              <Grid item xs={12} md={6}>
                <DetailItem label="Last Name" value={user.lastname} />
              </Grid>
              <Grid item xs={12} md={6}>
                <DetailItem label="Email" value={maskEmail(user.email)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <DetailItem label="Mobile" value={maskMobile(user.mobile)} />
              </Grid>
              <Grid item xs={12} md={6}>
                {!isSystem ? (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      MFA
                    </Typography>
                    <Box sx={{ mt: 0.25 }}>
                      <FormControlLabel
                        sx={{ ml: 0 }}
                        control={
                          <Switch
                            checked={Boolean(user.mfaEnabled)}
                            onChange={(e) => handleToggleMfa(e.target.checked)}
                            disabled={saving}
                          />
                        }
                        label={user.mfaEnabled ? "MFA enabled" : "MFA disabled"}
                      />
                    </Box>
                  </Box>
                ) : (
                  <DetailItem
                    label="MFA"
                    value={user.mfaEnabled ? "Enabled" : "Disabled"}
                  />
                )}
              </Grid>
            </Grid>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Account
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <StatusChip enabled={user.enabled} locked={user.locked} />
                </Box>
              </Grid>
              {user.type ? (
                <Grid item xs={12} sm={6}>
                  <DetailItem label="Type" value={user.type} />
                </Grid>
              ) : null}
              <Grid item xs={12} sm={6}>
                <DetailItem
                  label="Created Date"
                  value={formatEpochDate(user.creationDate)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DetailItem
                  label="Updated Date"
                  value={formatEpochDate(user.lastModifiedDate)}
                />
              </Grid>
              {user.lastLoggedInTs ? (
                <Grid item xs={12} sm={6}>
                  <DetailItem
                    label="Last Logged In"
                    value={formatEpochDate(user.lastLoggedInTs)}
                  />
                </Grid>
              ) : null}
            </Grid>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Roles
            </Typography>
            <ChipList
              items={user.roles}
              maxVisible={null}
              emptyLabel="No roles assigned"
              sx={{ flexWrap: "wrap", rowGap: 0.75, overflow: "visible" }}
            />
          </Box>
        </Stack>
      </Paper>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} flexWrap="wrap" useFlexGap>
        <Button
          variant="contained"
          onClick={() =>
            navigate(`${listPath}/edit/${encodeURIComponent(user.userId)}`)
          }
        >
          Edit User
        </Button>
        <Button variant="outlined" onClick={openRoleDialog}>
          Update Role
        </Button>
        <Button variant="outlined" onClick={() => setPasswordOpen(true)}>
          Change Password
        </Button>
        {!isSystem && user.locked ? (
          <Button variant="outlined" onClick={handleUnlock} disabled={saving}>
            Unlock User
          </Button>
        ) : null}
        <Button
          variant="outlined"
          color="error"
          onClick={() => setDeleteOpen(true)}
        >
          Delete User
        </Button>
      </Stack>

      <ChangePasswordDialog
        open={passwordOpen}
        user={user}
        loading={passwordLoading}
        onClose={() => setPasswordOpen(false)}
        onSubmit={handleChangePassword}
      />
      <UpdateRoleDialog
        open={roleOpen}
        user={user}
        roles={roles}
        loading={roleLoading}
        rolesLoading={rolesLoading}
        onClose={() => setRoleOpen(false)}
        onSubmit={handleUpdateRole}
      />
      <Dialog
        open={deleteOpen}
        onClose={saving ? undefined : () => setDeleteOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete user <strong>{user.username || user.userId}</strong>? This cannot
            be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={saving}
          >
            {saving ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserDetailsPage;
